import {
	useEffect,
	useState
} from "react";

export type TargetTracking = {
	targetRect: DOMRect | null;
	isTargetVisible: boolean;
};

export function useTargetTracking(target: HTMLElement, isEnabled: boolean): TargetTracking {
	const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
	const [isTargetVisible, setIsTargetVisible] = useState<boolean>(true);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!isEnabled) {
				return;
			}

			let lastRect: DOMRect = target.getBoundingClientRect();
			let lastChangeTimestamp: number = performance.now();
			let frameHandle: number | undefined = undefined;
			let timeoutHandle: SetTimeoutHandle | undefined = undefined;

			// Since scroll/resize doesn't necessarily lead to the target being repositioned right away,
			// we keep measuring until it settles
			const measure = (): void => {
				frameHandle = undefined;

				const rect: DOMRect = target.getBoundingClientRect();

				if (hasEqualBounds(rect, lastRect)) {
					if ((performance.now() - lastChangeTimestamp) > SETTLE_DURATION_IN_MS) {
						return;
					}
				} else {
					lastRect = rect;
					lastChangeTimestamp = performance.now();
					setTargetRect(rect);
				}

				// Throttle so we don't consume too many resources
				timeoutHandle = setTimeout(
					(): void => {
						timeoutHandle = undefined;
						frameHandle = requestAnimationFrame(measure);
					},
					MEASURE_INTERVAL_IN_MS
				);
			};

			const startMeasuring = (): void => {
				lastChangeTimestamp = performance.now();

				if (frameHandle === undefined && timeoutHandle === undefined) {
					frameHandle = requestAnimationFrame(measure);
				}
			};

			const cancellation = new AbortController();

			// NOTE: scroll does not bubble, so only a capture listener sees nested scroll containers.
			document.addEventListener(
				"scroll",
				startMeasuring,
				{
					capture: true,
					signal: cancellation.signal
				}
			);

			window.addEventListener(
				"resize",
				startMeasuring,
				{ signal: cancellation.signal }
			);

			const resizeObserver = new ResizeObserver(startMeasuring);
			resizeObserver.observe(target);

			// NOTE: a rootless observer applies every containing block's clip, not just the viewport's, so
			// it also catches a target clipped by its own on-screen scroll container.
			// https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
			const intersectionObserver = new IntersectionObserver(
				(entries: readonly IntersectionObserverEntry[]): void => {
					setIsTargetVisible(entries[entries.length - 1].isIntersecting);
				}
			);
			intersectionObserver.observe(target);

			startMeasuring();

			return (): void => {
				if (frameHandle !== undefined) {
					cancelAnimationFrame(frameHandle);
				}
				clearTimeout(timeoutHandle);
				cancellation.abort();
				resizeObserver.disconnect();
				intersectionObserver.disconnect();
				setTargetRect(null);
				setIsTargetVisible(true);
			};
		},
		[isEnabled, target]
	);

	return {
		targetRect,
		isTargetVisible: !isEnabled || isTargetVisible
	};
}

const MEASURE_INTERVAL_IN_MS = 32;
const SETTLE_DURATION_IN_MS = 250;

function hasEqualBounds(first: DOMRect, second: DOMRect): boolean {
	return first.top === second.top
		&& first.left === second.left
		&& first.width === second.width
		&& first.height === second.height;
}
